<script setup>
// UNB-681: an enabled arrow dimmed to 70% on hover, which read as going
// *inactive* on approach. Hover now holds full opacity; the disabled state is
// still the 30% one. NOTE: this control is shared by every 2026 carousel, so
// the change is global rather than Braindates-only.
//
// Second half of the same ticket, asked for twice: on hover the arrow itself
// also turns white (Nick, 2026-08-19 and 2026-08-20). The glyphs stroke with
// `currentColor`, inherited from the pill's text colour, so hover has to
// override that on the BUTTON — a wrapper-level rule would turn both arrows
// white when only one is hovered.
//
// That white belongs to the PINK variant only. It first went on unconditionally
// and so reached every 2026 carousel through this shared control — but pink is
// the dark-ground variant (braindates, related sessions on the session page),
// and on the magenta variant, which sits on light, a white arrow all but
// disappears. The session detail tray and the session page's speakers carousel
// both lost their arrows that way (Nick, Asana 1217801905363846 /
// 1217801905363850). The variant already encodes which ground we are on, so
// nothing new has to be passed in.
const props = defineProps({
    hasPrev: { type: Boolean, default: true },
    hasNext: { type: Boolean, default: true },
    variant: {
        type: String,
        default: 'magenta',
        validator: (v) => ['magenta', 'pink'].includes(v),
    },
    prevLabel: { type: String, default: 'Previous' },
    nextLabel: { type: String, default: 'Next' },
});

defineEmits(['prev', 'next']);

const colorClass = computed(() => props.variant === 'pink'
    ? 'border-[var(--pink)] text-[var(--pink)]'
    : 'border-[var(--magenta)] text-[var(--magenta)]');

// Applied to each BUTTON rather than the wrapper, for the reason above.
const hoverClass = computed(() => (props.variant === 'pink' ? 'hover:text-white' : ''));
</script>

<template>
    <div class="inline-flex items-center gap-[16px] rounded-full border w-[76px] h-[42px] px-[16px] justify-center"
        :class="colorClass">
        <button type="button"
            class="shrink-0 flex items-center justify-center
                cursor-pointer bg-transparent border-none p-0
                focus-visible:outline-none focus-visible:ring-2
                focus-visible:ring-current rounded-sm
                transition-[opacity,color] duration-200"
            :class="[hasPrev ? 'opacity-100' : 'opacity-30 pointer-events-none', hoverClass]"
            :aria-disabled="!hasPrev"
            :aria-label="prevLabel"
            @click="$emit('prev')">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M13 7H1M1 7L7.14 0.5M1 7L7.14 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>
        <button type="button"
            class="shrink-0 flex items-center justify-center
                cursor-pointer bg-transparent border-none p-0
                focus-visible:outline-none focus-visible:ring-2
                focus-visible:ring-current rounded-sm
                transition-[opacity,color] duration-200"
            :class="[hasNext ? 'opacity-100' : 'opacity-30 pointer-events-none', hoverClass]"
            :aria-disabled="!hasNext"
            :aria-label="nextLabel"
            @click="$emit('next')">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M1 7H13M13 7L6.86 0M13 7L6.86 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>
    </div>
</template>
